import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider,
    CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import { updateLiquidityProvider } from '../../api/lpApi';

// Validation schema
const validationSchema = yup.object({
    name: yup
        .string()
        .min(1, 'Name must be at least 1 character')
        .max(200, 'Name must be at most 200 characters')
        .required('Name is required'),
    isActive: yup.boolean(),
    priority: yup
        .number()
        .min(1, 'Priority must be at least 1')
        .max(100, 'Priority must be at most 100')
        .required('Priority is required'),
    lpType: yup
        .string()
        .max(50, 'LP Type must be at most 50 characters')
        .nullable(),
    region: yup
        .string()
        .max(10, 'Region must be at most 10 characters')
        .nullable(),
    isEcn: yup.boolean(),
    creditLimit: yup
        .number()
        .min(0, 'Credit limit must be at least 0')
        .nullable(),
    notes: yup
        .string()
        .max(1000, 'Notes must be at most 1000 characters')
        .nullable()
});

const EditLPModal = ({ open, onClose, onSuccess, lp }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const regions = [
        'US',
        'EU',
        'UK',
        'ASIA',
        'GLOBAL'
    ];

    // Initialize form with LP data
    const formik = useFormik({
        initialValues: {
            name: lp?.name || '',
            isActive: lp?.isActive || false,
            priority: lp?.priority || 1,
            lpType: lp?.lpType || '',
            region: lp?.region || '',
            isEcn: lp?.isEcn || false,
            creditLimit: lp?.creditLimit || 0,
            notes: lp?.notes || ''
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);

            try {
                // Get token from localStorage or context
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                // Prepare data for API
                const lpData = {
                    name: values.name,
                    isActive: values.isActive,
                    priority: values.priority,
                    lpType: values.lpType || null,
                    region: values.region || null,
                    isEcn: values.isEcn,
                    creditLimit: values.creditLimit || null,
                    notes: values.notes || null
                };

                // Call API
                const result = await updateLiquidityProvider(token, lp.id, lpData);
                
                toast.success('LP updated successfully!');
                onSuccess(result);
            } catch (error) {
                console.error('Error updating LP:', error);
                setError(error.message || 'Failed to update liquidity provider');
                toast.error('Failed to update liquidity provider');
            } finally {
                setLoading(false);
            }
        }
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open && lp) {
            formik.resetForm({
                values: {
                    name: lp.name || '',
                    isActive: lp.isActive || false,
                    priority: lp.priority || 1,
                    lpType: lp.lpType || '',
                    region: lp.region || '',
                    isEcn: lp.isEcn || false,
                    creditLimit: lp.creditLimit || 0,
                    notes: lp.notes || ''
                }
            });
            setError(null);
        }
    }, [open, lp]);

    const handleClose = () => {
        if (!loading) {
            formik.resetForm();
            setError(null);
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-lp-modal-title"
            aria-describedby="edit-lp-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
                maxWidth: 800,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EditIcon sx={{ color: '#ffd600', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                                Edit Liquidity Provider
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                Update LP information and settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                background: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid #d32f2f',
                                color: '#fff',
                                '& .MuiAlert-icon': { color: '#ff6b6b' }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon sx={{ fontSize: 20 }} />
                                    Basic Information
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="name"
                                    name="name"
                                    label="LP Name *"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: '#bfc9db' }}>LP Type</InputLabel>
                                    <Select
                                        id="lpType"
                                        name="lpType"
                                        value={formik.values.lpType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        sx={{
                                            color: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1de9b6' },
                                            '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                        }}
                                    >
                                        <MenuItem value="Prime Broker">Prime Broker</MenuItem>
                                        <MenuItem value="ECN Broker">ECN Broker</MenuItem>
                                        <MenuItem value="Exchange">Exchange</MenuItem>
                                        <MenuItem value="Market Maker">Market Maker</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="priority"
                                    name="priority"
                                    label="Priority (1-100) *"
                                    type="number"
                                    value={formik.values.priority}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.priority && Boolean(formik.errors.priority)}
                                    helperText={formik.touched.priority && formik.errors.priority}
                                    inputProps={{ min: 1, max: 100 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: '#bfc9db' }}>Region</InputLabel>
                                    <Select
                                        id="region"
                                        name="region"
                                        value={formik.values.region}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.region && Boolean(formik.errors.region)}
                                        sx={{
                                            color: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1de9b6' },
                                            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b6b' },
                                            '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                        }}
                                    >
                                        <MenuItem value="">Select Region</MenuItem>
                                        {regions.map((region) => (
                                            <MenuItem key={region} value={region}>{region}</MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.region && formik.errors.region && (
                                        <Typography variant="caption" sx={{ color: '#ff6b6b', mt: 0.5, ml: 2 }}>
                                            {formik.errors.region}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    id="creditLimit"
                                    name="creditLimit"
                                    label="Credit Limit"
                                    type="number"
                                    value={formik.values.creditLimit}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.creditLimit && Boolean(formik.errors.creditLimit)}
                                    helperText={formik.touched.creditLimit && formik.errors.creditLimit}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="notes"
                                    name="notes"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={formik.values.notes}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.notes && Boolean(formik.errors.notes)}
                                    helperText={formik.touched.notes && formik.errors.notes}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                        },
                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                            </Grid>

                            {/* Status Settings */}
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: '#1de9b6', mb: 2, mt: 2 }}>
                                    Status Settings
                                </Typography>
                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isActive"
                                            name="isActive"
                                            checked={formik.values.isActive}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#1de9b6',
                                                '&.Mui-checked': { color: '#1de9b6' }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: '#fff' }}>
                                            Active Status
                                        </Typography>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="isEcn"
                                            name="isEcn"
                                            checked={formik.values.isEcn}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: '#1de9b6',
                                                '&.Mui-checked': { color: '#1de9b6' }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: '#fff' }}>
                                            ECN Provider
                                        </Typography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                            background: '#ffd600',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#ffcc02'
                            },
                            '&:disabled': {
                                background: 'rgba(255, 214, 0, 0.3)',
                                color: 'rgba(0, 0, 0, 0.3)'
                            }
                        }}
                    >
                        {loading ? 'Updating...' : 'Update LP'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditLPModal;
